/**
 * Gtag Consent Gate Plugin
 * 
 * Prevents Google Tag Manager/GA script from loading when consent is rejected.
 * This plugin runs early (00- prefix) to intercept script loading before nuxt-gtag initializes.
 * 
 * When consent is required and user has rejected it, we prevent the gtag script from loading.
 */
export default defineNuxtPlugin({
  name: 'gtag-consent-gate',
  enforce: 'pre', // Run before other plugins
  setup() {
    if (!process.client) return

    // Run immediately, synchronously
    const config = useRuntimeConfig()
    const requireConsent = config.public.requireConsent === true

    // If consent is not required, let gtag load normally
    if (!requireConsent) {
      return
    }

    // Check consent status immediately
    const consentCookie = useCookie<boolean | null>('analytics_consent', { default: () => null })
    
    // If user has explicitly rejected consent (value is false), prevent gtag from loading
    if (consentCookie.value === false) {
      // Intercept document.createElement to prevent gtag scripts from being created
      const originalCreateElement = document.createElement.bind(document)
      document.createElement = function(tagName: string, options?: ElementCreationOptions) {
        const element = originalCreateElement(tagName, options)
        
        // If it's a script tag, intercept src assignment
        if (tagName.toLowerCase() === 'script') {
          const script = element as HTMLScriptElement
          let scriptSrc = ''
          
          // Override src setter to block gtag scripts
          Object.defineProperty(script, 'src', {
            get: () => scriptSrc,
            set: (value: string) => {
              // Block gtag/gtm and Chatwoot scripts if consent is rejected
              if (value && (
                value.includes('googletagmanager.com') ||
                value.includes('google-analytics.com') ||
                value.includes('/gtag/js') ||
                value.includes('gtag/js') ||
                value.includes('chatwoot.com') ||
                value.includes('app.chatwoot.com')
              )) {
                if (import.meta.env.DEV) {
                  console.log('🚫 Blocked script creation - consent rejected:', value)
                }
                // Don't set src, effectively preventing the script from loading
                return
              }
              scriptSrc = value
              // Call original setter if it exists
              if (Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src')?.set) {
                Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src')?.set?.call(script, value)
              }
            },
            configurable: true,
            enumerable: true
          })
        }
        
        return element
      }

    // Remove any existing gtag and Chatwoot scripts and preload links immediately
    const removeBlockedScripts = () => {
      // Remove blocked scripts
      const blockedScripts = document.querySelectorAll(
        'script[src*="googletagmanager.com"], ' +
        'script[src*="google-analytics.com"], ' +
        'script[src*="/gtag/js"], ' +
        'script[src*="gtag/js"], ' +
        'script[src*="chatwoot.com"], ' +
        'script[src*="app.chatwoot.com"]'
      )
      blockedScripts.forEach((script) => {
        script.remove()
        if (import.meta.env.DEV) {
          console.log('🚫 Removed blocked script - consent rejected')
        }
      })
      
      // Also remove preload/prefetch links for blocked resources
      const blockedLinks = document.querySelectorAll(
        'link[rel="preload"][href*="googletagmanager.com"], ' +
        'link[rel="preload"][href*="google-analytics.com"], ' +
        'link[rel="prefetch"][href*="googletagmanager.com"], ' +
        'link[rel="prefetch"][href*="google-analytics.com"], ' +
        'link[rel="preload"][href*="chatwoot.com"], ' +
        'link[rel="preload"][href*="app.chatwoot.com"], ' +
        'link[rel="prefetch"][href*="chatwoot.com"], ' +
        'link[rel="prefetch"][href*="app.chatwoot.com"]'
      )
      blockedLinks.forEach((link) => {
        link.remove()
        if (import.meta.env.DEV) {
          console.log('🚫 Removed blocked preload/prefetch link - consent rejected')
        }
      })
    }

    // Remove scripts that might already be in the DOM
    removeBlockedScripts()
    
    // Also intercept appendChild/insertBefore to catch scripts added via other methods
    const originalAppendChild = Node.prototype.appendChild
    const originalInsertBefore = Node.prototype.insertBefore
    
    Node.prototype.appendChild = function<T extends Node>(newChild: T): T {
      // Check if it's a script with blocked URL
      if (newChild instanceof HTMLScriptElement) {
        const src = (newChild as HTMLScriptElement).src || (newChild as HTMLScriptElement).getAttribute('src') || ''
        if (src && (
          src.includes('googletagmanager.com') ||
          src.includes('google-analytics.com') ||
          src.includes('/gtag/js') ||
          src.includes('gtag/js') ||
          src.includes('chatwoot.com') ||
          src.includes('app.chatwoot.com')
        )) {
          if (import.meta.env.DEV) {
            console.log('🚫 Blocked script appendChild - consent rejected:', src)
          }
          return newChild // Return without appending
        }
      }
      // Call original method with correct 'this' context
      return originalAppendChild.call(this, newChild) as T
    }
    
    Node.prototype.insertBefore = function<T extends Node>(newChild: T, refChild: Node | null): T {
      // Check if it's a script with blocked URL
      if (newChild instanceof HTMLScriptElement) {
        const src = (newChild as HTMLScriptElement).src || (newChild as HTMLScriptElement).getAttribute('src') || ''
        if (src && (
          src.includes('googletagmanager.com') ||
          src.includes('google-analytics.com') ||
          src.includes('/gtag/js') ||
          src.includes('gtag/js') ||
          src.includes('chatwoot.com') ||
          src.includes('app.chatwoot.com')
        )) {
          if (import.meta.env.DEV) {
            console.log('🚫 Blocked script insertBefore - consent rejected:', src)
          }
          return newChild // Return without inserting
        }
      }
      // Call original method with correct 'this' context
      return originalInsertBefore.call(this, newChild, refChild) as T
    }

    // Watch for new blocked scripts being added and remove them immediately
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement
            // Check if it's a script tag with blocked URLs
            if (element.tagName === 'SCRIPT') {
              const script = element as HTMLScriptElement
              const src = script.src || script.getAttribute('src') || ''
              if (src && (
                src.includes('googletagmanager.com') ||
                src.includes('google-analytics.com') ||
                src.includes('/gtag/js') ||
                src.includes('gtag/js') ||
                src.includes('chatwoot.com') ||
                src.includes('app.chatwoot.com')
              )) {
                script.remove()
                if (import.meta.env.DEV) {
                  console.log('🚫 Blocked script from loading - consent rejected:', src)
                }
              }
            }
            // Also check for nested scripts and links
            const nestedScripts = element.querySelectorAll?.(
              'script[src*="googletagmanager.com"], ' +
              'script[src*="google-analytics.com"], ' +
              'script[src*="chatwoot.com"], ' +
              'script[src*="app.chatwoot.com"]'
            )
            nestedScripts?.forEach((script) => {
              script.remove()
              if (import.meta.env.DEV) {
                console.log('🚫 Removed nested blocked script - consent rejected')
              }
            })
            
            // Also check for preload/prefetch links
            const nestedLinks = element.querySelectorAll?.(
              'link[rel="preload"][href*="googletagmanager.com"], ' +
              'link[rel="preload"][href*="google-analytics.com"], ' +
              'link[rel="prefetch"][href*="googletagmanager.com"], ' +
              'link[rel="prefetch"][href*="google-analytics.com"], ' +
              'link[rel="preload"][href*="chatwoot.com"], ' +
              'link[rel="preload"][href*="app.chatwoot.com"], ' +
              'link[rel="prefetch"][href*="chatwoot.com"], ' +
              'link[rel="prefetch"][href*="app.chatwoot.com"]'
            )
            nestedLinks?.forEach((link) => {
              link.remove()
              if (import.meta.env.DEV) {
                console.log('🚫 Removed nested blocked preload/prefetch link - consent rejected')
              }
            })
          }
        })
      })
    })

    // Observe the document head and body for script additions
    observer.observe(document.head, { childList: true, subtree: true })
    observer.observe(document.body, { childList: true, subtree: true })

    // Also check periodically (as a fallback) - more aggressive
    const intervalId = setInterval(() => {
      removeBlockedScripts()
    }, 500)

    // Prevent gtag function from being called
    if (typeof window !== 'undefined') {
      // Override window.gtag if it exists or will exist
      const noopGtag = function() {
        if (import.meta.env.DEV) {
          console.log('🚫 gtag() call blocked - consent rejected')
        }
        // No-op: do nothing
      }
      
      // Set gtag as a no-op before nuxt-gtag initializes it
      ;(window as any).gtag = noopGtag
      
      // Also prevent it from being overwritten - use a Proxy to catch any attempts
      const gtagProxy = new Proxy(noopGtag, {
        apply: function(target, thisArg, argumentsList) {
          if (import.meta.env.DEV) {
            console.log('🚫 gtag() call blocked via proxy - consent rejected')
          }
          return undefined
        },
        set: function(target, property, value) {
          // Allow setting properties but log it
          if (import.meta.env.DEV) {
            console.log('🚫 Attempted to set gtag property - consent rejected')
          }
          return false
        }
      })
      
      try {
        Object.defineProperty(window, 'gtag', {
          value: gtagProxy,
          writable: false, // Prevent overwrite
          configurable: false
        })
      } catch (e) {
        // If we can't define it, use a setter/getter approach
        try {
          Object.defineProperty(window, 'gtag', {
            get: () => gtagProxy,
            set: () => {
              if (import.meta.env.DEV) {
                console.log('🚫 Attempted to overwrite gtag - consent rejected')
              }
              // Don't allow overwrite
            },
            configurable: false
          })
        } catch (e2) {
          // Fallback: just set it directly
          ;(window as any).gtag = gtagProxy
        }
      }

      // Override dataLayer to prevent tracking
      if (!(window as any).dataLayer) {
        ;(window as any).dataLayer = []
      }
      const originalPush = (window as any).dataLayer.push.bind((window as any).dataLayer)
      ;(window as any).dataLayer.push = function(...args: any[]) {
        // Block gtag-related pushes
        if (args.length > 0 && typeof args[0] === 'string' && (
          args[0] === 'consent' || 
          args[0] === 'config' || 
          args[0] === 'event' ||
          args[0] === 'js' ||
          args[0] === 'set'
        )) {
          if (import.meta.env.DEV) {
            console.log('🚫 dataLayer.push blocked - consent rejected:', args[0])
          }
          return (window as any).dataLayer.length // Return array length to mimic normal behavior
        }
        // Allow other pushes (but they won't be processed by gtag anyway)
        return originalPush(...args)
      }

      // Clean up on page hide (doesn't block bf-cache like beforeunload)
      // Use pagehide instead of beforeunload to allow back/forward cache
      window.addEventListener('pagehide', () => {
        observer.disconnect()
        clearInterval(intervalId)
      }, { passive: true })
    }

      if (import.meta.env.DEV) {
        console.log('🚫 Gtag and Chatwoot script loading blocked - user rejected consent')
      }
    }
  }
})
